// Add-on accessories on the storefront: the maths and the URL format.
//
// The offered add-ons arrive on the product record as `accessory_options`
// ([{ slug, name, price, image, short_description, included }]), resolved by
// the server from the product's `accessories` field. The server is also what
// charges for them: checkout sends slugs and quantities only, and
// server/src/utils/accessories.js prices them from the database. Everything
// here is display - it must agree with that module, never replace it.
//
// The configurator and the product-page widget build the checkout link through
// encodeAddons; the checkout page reads it back through parseAddons. One
// format, one place: `addons=awning-wifi-controller:2,other-slug:1`.

// Mirrors MAX_ADDON_QTY in server/src/utils/accessories.js.
export const MAX_ADDON_QTY = 10;

function clampQty(n) {
  const q = Math.floor(Number(n));
  if (!Number.isFinite(q) || q < 0) return 0;
  return Math.min(q, MAX_ADDON_QTY);
}

// { slug: qty } -> "slug:qty,slug:qty". Zero quantities are left out.
export function encodeAddons(quantities) {
  return Object.entries(quantities || {})
    .map(([slug, qty]) => [slug, clampQty(qty)])
    .filter(([slug, qty]) => slug && qty > 0)
    .map(([slug, qty]) => `${slug}:${qty}`)
    .join(',');
}

// "slug:qty,..." -> { slug: qty }. Tolerant of junk; the server decides.
export function parseAddons(param) {
  const out = {};
  for (const part of String(param || '').split(',')) {
    const [slug, qty] = part.split(':');
    const q = clampQty(qty);
    if (slug && q > 0) out[slug.trim().toLowerCase()] = q;
  }
  return out;
}

// { slug: qty } -> the body checkout sends: [{ slug, quantity }].
export function addonsPayload(quantities) {
  return Object.entries(quantities || {})
    .map(([slug, qty]) => ({ slug, quantity: clampQty(qty) }))
    .filter((a) => a.quantity > 0);
}

// The lines an order will carry, as the server will price them: every
// included accessory once at no charge, then each chosen paid one. Returns
// { lines: [{ slug, name, image, quantity, unitPrice, included, lineTotal }], total }.
export function addonLines(options, quantities) {
  const lines = [];
  let total = 0;
  for (const opt of options || []) {
    if (opt.included) {
      lines.push({ ...lineBase(opt), quantity: 1, included: true, lineTotal: 0 });
      continue;
    }
    const quantity = clampQty(quantities?.[opt.slug]);
    if (!quantity || opt.price == null) continue;
    const lineTotal = quantity * opt.price;
    lines.push({ ...lineBase(opt), quantity, included: false, lineTotal });
    total += lineTotal;
  }
  return { lines, total };
}

function lineBase(opt) {
  return { slug: opt.slug, name: opt.name, image: opt.image, unitPrice: opt.price };
}
